import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Logo from "../../public/eventli-logo.svg";
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SigninPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Slides content
  const slides = [
    {
      title: "Welcome to Eventli",
      contents: [
        "Find & Book Events Easily -- Explore exciting events and secure your tickets in seconds.",
        "Earn Rewards -- Get Eventli Coins for attending, sharing, reviewing, and referring friends. Use them for discounts!",
        "Engage & Connect -- Like, share, follow, and interact with event communities."
      ]
    },
    {
      title: "Welcome to Eventli",
      contents: [
        "Manage Your Wallet & Portfolio -- Load up your Eventli Wallet for quick payments and track your event history.",
        "Exclusive Access -- Get early access to premium events and special offers.",
        "Personalized Recommendations -- Discover events tailored to your interests."
      ]
    },
  ];

  // Auto slide functionality - only run on desktop
  useEffect(() => {
    // Check if window width is greater than 768px (tablet and up)
    const isDesktop = window.innerWidth >= 768;
    let interval;
    
    if (isDesktop) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
    }
    
    const handleResize = () => {
      if (window.innerWidth < 768 && interval) {
        clearInterval(interval);
      } else if (window.innerWidth >= 768 && !interval) {
        interval = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, [slides.length]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('https://event-li-backend.onrender.com/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.msg || 'Login failed');
      }

     // Save JWT and user details to localStorage
     localStorage.setItem('userjwt', data.token);
     localStorage.setItem('userdetails', JSON.stringify(data.user));

      // Show success toast
      toast.success('Login successful! Redirecting...', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        onClose: () => navigate('/dashboard')
      });

    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen">
      {/* Toast Container */}
      <ToastContainer />

      {/* Left Side - Sliding Content - Hidden on mobile */}
      <motion.div 
        className="hidden md:flex md:w-1/2 bg-navy-blue p-4 md:p-8 flex-col justify-center relative overflow-hidden"
        initial={{ backgroundColor: "#0a0a3c" }}
        animate={{ backgroundColor: "#0a0a3c" }}
        transition={{ duration: 0.5 }}
      >
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-24 h-24 bg-[#FCD20B] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-[#FCD20B] rounded-full translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-20 right-0 w-32 h-32 border-2 border-[#A19EFF] opacity-30 rounded-lg transform rotate-12"></div>
        <div className="absolute bottom-20 left-0 w-32 h-32 border-2 border-[#A19EFF] opacity-30 rounded-lg transform -rotate-12"></div>
        
        <div className="z-10 px-4 md:px-8 py-8 md:py-16 h-full flex items-center">
          {slides.map((slide, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 100 }}
              animate={{ 
                opacity: currentSlide === index ? 1 : 0,
                x: currentSlide === index ? 0 : 1000,
                display: currentSlide === index ? 'flex' : 'none'
              }}
              transition={{ duration: 0.10, ease: "easeInOut" }}
              className="text-white flex flex-col justify-center h-full"
            >
              <h2 className="text-[22px] md:text-[24px] font-semibold text-center mb-6 md:mb-8">{slide.title}</h2>
              <div className="space-y-3 md:space-y-4">
                {slide.contents.map((content, i) => (
                  <p key={i} className="text-[16px] md:text-[18px] font-thin">{content}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Slide indicators */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-1 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'w-8 bg-white' : 'w-4 bg-gray-400'
              }`}
            />
          ))}
        </div>
      </motion.div>
      
      {/* Right Side - Signup Form */}
      <motion.div 
        className="w-full md:w-1/2 bg-white p-5 sm:p-6 md:p-8 flex flex-col justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Mobile-only logo with blue background at the top */}
        <div className="md:hidden w-full bg-navy-blue py-4 px-4 mb-6 -mt-5 -mx-5">
          <div className="flex justify-center items-center">
            <img src={Logo} alt="EventLi Logo" className="h-10" />
          </div>
        </div>
        
        <div className="max-w-md mx-auto w-full px-2 sm:px-0">
          <div className="mb-6 text-center hidden md:flex justify-center">
            <h1 className="text-3xl font-bold text-navy-blue">
              <img src={Logo} alt="EventLi Logo" />
            </h1>
          </div>
          
          <h2 className="text-[22px] md:text-[24px] font-medium mb-6 md:mb-8 text-center">Sign in</h2>
          
          <form className="space-y-4 md:space-y-5" onSubmit={handleSubmit}>           
            <div>
              <label htmlFor="email" className="block text-gray-700 mb-2 text-sm md:text-base">Email Address</label>
              <motion.input 
                whileFocus={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300 }}
                type="email" 
                id="email" 
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400" 
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-gray-700 mb-2 text-sm md:text-base">Password</label>
              <div className="relative">
                <motion.input 
                  whileFocus={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  type={showPassword ? "text" : "password"} 
                  id="password" 
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400" 
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button 
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg> : 
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  }
                </button>
              </div>
            </div>
            
            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full rounded-[30px] py-2.5 md:py-3 px-4 text-[16px] md:text-[18px] bg-[#FCD20B] hover:bg-yellow-500 text-black font-medium transition duration-300 ease-in-out flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </motion.button>
          </form>
          
          <div className="mt-2 text-center">
            <p className="text-gray-600 text-[12px] md:text-[14px]">
              Don't have an account? <Link to="/signup" className="text-[#FCD20B] hover:underline">Sign up</Link>
            </p>
          </div>
          
          <div className="mt-6 md:mt-8">
            <div className="flex items-center justify-center">
              <div className="flex-grow h-px bg-gray-300"></div>
              <span className="px-4 text-xs md:text-sm text-gray-500">Or sign in with</span>
              <div className="flex-grow h-px bg-gray-300"></div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3 mt-4 md:mt-5">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center px-3 md:px-4 h-[40px] md:h-[45px] border border-[#E3E3E3] rounded-full"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2275_11961)">
                    <path d="M8.69405 1.07752C6.29608 1.9094 4.22807 3.48833 2.79378 5.5824C1.3595 7.67646 0.634534 10.1753 0.725379 12.7118C0.816225 15.2484 1.71809 17.6889 3.29851 19.675C4.87892 21.6611 7.05458 23.0881 9.50592 23.7463C11.4933 24.2591 13.5754 24.2816 15.5734 23.8119C17.3834 23.4053 19.0568 22.5357 20.4297 21.2881C21.8586 19.95 22.8957 18.2478 23.4297 16.3644C24.01 14.3163 24.1133 12.1624 23.7316 10.0681H12.5715V14.6975H19.0347C18.9055 15.4359 18.6287 16.1406 18.2208 16.7695C17.813 17.3983 17.2824 17.9385 16.6609 18.3575C15.8717 18.8796 14.982 19.2308 14.0491 19.3888C13.1133 19.5628 12.1535 19.5628 11.2178 19.3888C10.2694 19.1927 9.37223 18.8013 8.58342 18.2394C7.3162 17.3424 6.36469 16.068 5.86467 14.5981C5.3562 13.1008 5.3562 11.4774 5.86467 9.98002C6.2206 8.93042 6.80899 7.97477 7.58592 7.18439C8.47503 6.26329 9.60067 5.60489 10.8393 5.28141C12.078 4.95793 13.3818 4.98189 14.6078 5.35064C15.5655 5.64463 16.4413 6.15828 17.1653 6.85064C17.894 6.12564 18.6216 5.39877 19.3478 4.67002C19.7228 4.27814 20.1315 3.90502 20.5009 3.50377C19.3957 2.47529 18.0985 1.675 16.6834 1.14877C14.1066 0.213124 11.2871 0.18798 8.69405 1.07752Z" fill="white"/>
                    <path d="M8.69371 1.07745C11.2866 0.18731 14.1061 0.211793 16.6831 1.14683C18.0984 1.67663 19.3951 2.48077 20.4987 3.51308C20.1237 3.91433 19.7281 4.28933 19.3456 4.67933C18.6181 5.40558 17.8912 6.12933 17.165 6.85058C16.4409 6.15821 15.5652 5.64456 14.6075 5.35058C13.3819 4.98053 12.0781 4.95519 10.8391 5.27735C9.60012 5.5995 8.4738 6.25669 7.58371 7.17683C6.80677 7.96721 6.21838 8.92286 5.86246 9.97245L1.97559 6.96308C3.36685 4.20413 5.77574 2.09375 8.69371 1.07745Z" fill="#E33629"/>
                    <path d="M0.944409 9.94414C1.15332 8.90875 1.50017 7.90606 1.97566 6.96289L5.86253 9.97977C5.35406 11.4772 5.35406 13.1005 5.86253 14.5979C4.56753 15.5979 3.27191 16.6029 1.97566 17.6129C0.785316 15.2435 0.422282 12.5438 0.944409 9.94414Z" fill="#F8BD00"/>
                    <path d="M12.5711 10.0664H23.7311C24.1129 12.1607 24.0096 14.3145 23.4293 16.3627C22.8953 18.2461 21.8582 19.9483 20.4293 21.2864C19.1749 20.3077 17.9149 19.3364 16.6605 18.3577C17.2824 17.9382 17.8132 17.3975 18.2211 16.7679C18.629 16.1384 18.9056 15.4329 19.0343 14.6939H12.5711C12.5693 13.1527 12.5711 11.6095 12.5711 10.0664Z" fill="#587DBD"/>
                    <path d="M1.97363 17.6131C3.26988 16.6131 4.56551 15.6081 5.86051 14.5981C6.36152 16.0685 7.31439 17.3429 8.58301 18.2394C9.37427 18.7986 10.2734 19.1869 11.223 19.3794C12.1587 19.5534 13.1185 19.5534 14.0543 19.3794C14.9872 19.2215 15.8769 18.8702 16.6661 18.3481C17.9205 19.3269 19.1805 20.2981 20.4349 21.2769C19.0622 22.5251 17.3888 23.3954 15.5786 23.8025C13.5806 24.2722 11.4985 24.2497 9.51113 23.7369C7.93932 23.3172 6.47115 22.5774 5.19863 21.5638C3.85175 20.4944 2.75167 19.1468 1.97363 17.6131Z" fill="#319F43"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_2275_11961">
                      <rect width="24" height="24" fill="white" transform="translate(0.333008 0.288086)"/>
                    </clipPath>
                  </defs>
                </svg>
                <span className='text-[14px] md:text-[16px] font-medium text-[#7F7F7F]'>Google</span>
              </motion.button>
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center px-3 md:px-4 h-[40px] md:h-[45px] border border-[#E3E3E3] rounded-full"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 0.288086H2C0.9 0.288086 0.00999999 1.18809 0.00999999 2.28809L0 14.2881C0 15.3881 0.9 16.2881 2 16.2881H18C19.1 16.2881 20 15.3881 20 14.2881V2.28809C20 1.18809 19.1 0.288086 18 0.288086ZM17.6 4.53809L10.53 8.95809C10.21 9.15809 9.79 9.15809 9.47 8.95809L2.4 4.53809C2.29973 4.4818 2.21192 4.40575 2.14189 4.31454C2.07186 4.22333 2.02106 4.11886 1.99258 4.00746C1.96409 3.89605 1.9585 3.78002 1.97616 3.66639C1.99381 3.55276 2.03434 3.4439 2.09528 3.34639C2.15623 3.24887 2.23632 3.16474 2.33073 3.09908C2.42513 3.03342 2.53187 2.9876 2.6445 2.96438C2.75712 2.94117 2.87328 2.94105 2.98595 2.96403C3.09863 2.98702 3.20546 3.03262 3.3 3.09809L10 7.28809L16.7 3.09809C16.7945 3.03262 16.9014 2.98702 17.014 2.96403C17.1267 2.94105 17.2429 2.94117 17.3555 2.96438C17.4681 2.9876 17.5749 3.03342 17.6693 3.09908C17.7637 3.16474 17.8438 3.24887 17.9047 3.34639C17.9657 3.4439 18.0062 3.55276 18.0238 3.66639C18.0415 3.78002 18.0359 3.89605 18.0074 4.00746C17.9789 4.11886 17.9281 4.22333 17.8581 4.31454C17.7881 4.40575 17.7003 4.4818 17.6 4.53809Z" fill="black"/>
                </svg>
                <span className='text-[14px] md:text-[16px] font-medium text-[#7F7F7F]'>Email</span>
              </motion.button>
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center px-3 md:px-4 h-[40px] md:h-[45px] border border-[#E3E3E3] rounded-full"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#1877F2"
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
                <span className='text-[14px] md:text-[16px] font-medium text-[#7F7F7F]'>Facebook</span>
              </motion.button>
            </div>
          </div>
          
          <div className="mt-4 text-center text-[12px] md:text-[14px] font-normal text-gray-500">
            By Signing up, I agree to EventLi's 
            <a href="#" className="text-[#06044B] hover:underline ml-1">Privacy Policy</a> and 
            <a href="#" className="text-[#06044B] hover:underline ml-1">Terms of Service</a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SigninPage;